import React, {useEffect, useMemo, useState} from 'react';
import type {GuessModelV1} from '@albert-io/models';
import {SupplementChip} from 'client/components/QuestionTypes/shared/DraggableChip';
import {useShallow} from 'zustand/react/shallow';
import type {DragEndEvent, DragOverEvent, DragStartEvent} from '@dnd-kit/core';
import DndContextWrapper from 'client/components/QuestionTypes/shared/DndContextWrapper';
import {useQuestionStore} from 'client/components/QuestionTypes/V2/useQuestionStores';
import useQuestionAndDrafts from '../../V2/useQuestionAndDrafts.hook';
import ItemList from './ItemList.react';
import CorrectAnswerSection from './CorrectAnswerSection.react';
import {dividerIndexFrom, orderingAnnouncements} from './announcements';
interface Props {
  question: Record<string, any>;
  isGuessSubmitted?: boolean;
  guess?: GuessModelV1 | null;
}

const OrderingBankQuestion = ({question, isGuessSubmitted, guess}: Props) => {
  const {initialize, saveDraftGuess, addGuessInfo} = useQuestionAndDrafts(question);

  const useStoreHook = useQuestionStore<'ordering'>(question);

  const {
    questionInStore,
    items,
    setIsDragging,
    setActiveItemId,
    setHoveredDividerId,
    moveItemToPosition,
    setDraggedItemId,
    setPreviewTargetIndex,
    isSubmitted
    // oxlint-disable-next-line react/hooks -- useStoreHook comes from setUpQuestionStore's registry (cached zustand hook keyed by store name); its identity is stable for a mounted component
  } = useStoreHook(
    useShallow((state) => ({
      questionInStore: state.question,
      items: state.items,
      setIsDragging: state.setIsDragging,
      setActiveItemId: state.setActiveItem,
      setHoveredDividerId: state.setHoveredDividerId,
      moveItemToPosition: state.moveItemToPosition,
      setDraggedItemId: state.setDraggedItemId,
      setPreviewTargetIndex: state.setPreviewTargetIndex,
      isSubmitted: state.isSubmitted
    }))
  );

  const onDragStart = (event: DragStartEvent) => {
    setIsDragging(true);
    setActiveItemId(null);
    setDraggedItemId(event.active.id as string);
  };

  const onDragOver = (event: DragOverEvent) => {
    const targetIndex = dividerIndexFrom(event.over?.id);
    setHoveredDividerId(targetIndex === null ? null : `divider-${targetIndex}`);
    setPreviewTargetIndex(targetIndex);
  };

  const endDrag = () => {
    setIsDragging(false);
    setHoveredDividerId(null);
    setDraggedItemId(null);
    setPreviewTargetIndex(null);
  };

  const onDrop = (event: DragEndEvent) => {
    const {active, over} = event;
    endDrag();

    const targetIndex = dividerIndexFrom(over?.id);
    if (targetIndex === null) return;

    moveItemToPosition(active.id as string, targetIndex);
    saveDraftGuess();
  };

  const [dragProgress] = useState({hasLeftOrigin: false});
  const announcements = useMemo(
    () => orderingAnnouncements(items, dragProgress),
    [items, dragProgress]
  );

  useEffect(() => {
    if (question && question.size > 0) {
      if (isGuessSubmitted && guess) {
        addGuessInfo(guess);
      } else {
        // Explicitly clear any stale submission state from a previously viewed question
        addGuessInfo(null);
      }
      initialize(question);
    }
  }, [question, initialize, isGuessSubmitted, guess, addGuessInfo]);

  if (!questionInStore) return null;

  return (
    <DndContextWrapper
      onDragStart={onDragStart}
      onDragOver={onDragOver}
      onDragEnd={onDrop}
      onDragCancel={endDrag}
      renderOverlay={(activeId) => {
        const item = items.find((i) => i.id === activeId);
        return item ? <SupplementChip value={item.value} size='small' /> : null;
      }}
      accessibility={{announcements}}
    >
      <div className='flex flex-col'>
        {/* Ordering Bank */}
        <ItemList question={questionInStore} />
        {isSubmitted && <CorrectAnswerSection question={questionInStore} />}
      </div>
    </DndContextWrapper>
  );
};

export default OrderingBankQuestion;
